import { useTranslation } from 'react-i18next'
import type { LucideIcon } from 'lucide-react'
import { Search } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { formatNumber } from '@/lib/utils'

export function StatCard({
  label,
  value,
  icon: Icon,
  hint,
  tone = 'primary',
  onClick,
}: {
  label: string
  value: number | string
  icon?: LucideIcon
  hint?: string
  tone?: 'primary' | 'success' | 'warning' | 'destructive'
  /** Có hàm này thì cả thẻ bấm được để mở bảng truy vết số liệu. */
  onClick?: () => void
}) {
  const { t } = useTranslation('sharedMisc')
  const toneMap = {
    primary: 'bg-primary/10 text-primary',
    success: 'bg-success/10 text-success',
    warning: 'bg-warning/10 text-warning',
    destructive: 'bg-destructive/10 text-destructive',
  }
  return (
    <Card
      className={cn(
        // Điện thoại: thẻ gọn để xếp 2 cột (kiểu thẻ số liệu Lark Mobile).
        'p-3.5 sm:p-5',
        onClick && 'group cursor-pointer transition-shadow hover:border-primary/40 hover:shadow-md',
      )}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } } : undefined}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground sm:text-sm">{label}</p>
          <p className="mt-1.5 text-2xl font-bold tabular-nums sm:mt-2 sm:text-3xl">
            {typeof value === 'number' ? formatNumber(value) : value}
          </p>
          {hint && <p className="mt-1 line-clamp-2 text-[11px] leading-snug text-muted-foreground sm:text-xs">{hint}</p>}
          {onClick && (
            <p className="mt-1 hidden items-center gap-1 text-[11px] text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 sm:inline-flex">
              <Search className="h-3 w-3" />
              {t('statCard.clickToTrace')}
            </p>
          )}
        </div>
        {Icon && (
          <div className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg sm:h-10 sm:w-10', toneMap[tone])}>
            <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
          </div>
        )}
      </div>
    </Card>
  )
}
